import { useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { adsEnabled } from "@/lib/adsense";
import { AdUnit } from "@/components/AdUnit";
import { Gift, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const AD_SLOT_REWARD = process.env.REACT_APP_AD_SLOT_REWARD || "";
const WATCH_SECS = 15;

// "Watch an ad -> earn a few free credits" for logged-in users. Hidden entirely
// unless ads are enabled + configured. Reward/caps are enforced server-side.
export function RewardedAdCard() {
  const { user, setCredits } = useAuth();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [left, setLeft] = useState(WATCH_SECS);
  const [claiming, setClaiming] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!adsEnabled || !user) return;
    api.get("/ads/reward/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, [user]);

  if (!adsEnabled || !user || !status || !/^\d+$/.test(AD_SLOT_REWARD)) return null;

  const startWatch = () => {
    setOpen(true);
    setLeft(WATCH_SECS);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setLeft((s) => {
        if (s <= 1) { clearInterval(timerRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  const claim = async () => {
    setClaiming(true);
    try {
      const eventId = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
      const { data } = await api.post("/ads/reward/claim", { event_id: eventId });
      if (typeof data.credits === "number") setCredits(data.credits);
      setStatus((s) => ({ ...s, remaining: data.remaining }));
      toast.success(`+${data.credited} credit${data.credited === 1 ? "" : "s"} added!`);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add credits — try again.");
    } finally {
      setClaiming(false);
    }
  };

  const done = status.remaining <= 0;

  return (
    <>
      <div className="rounded-2xl border border-primary/25 bg-primary/5 p-4 flex items-center justify-between gap-4" data-testid="rewarded-ad-card">
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-10 w-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
            <Gift className="h-5 w-5 text-primary" />
          </span>
          <div className="min-w-0">
            <p className="font-heading text-sm font-black text-white">Free credits on us</p>
            <p className="text-xs text-muted-foreground">
              {done ? "You've claimed today's free credits — back tomorrow!" : `Watch a short ad, get +${status.reward} credit${status.reward === 1 ? "" : "s"}. ${status.remaining} left today.`}
            </p>
          </div>
        </div>
        <Button onClick={startWatch} disabled={done} data-testid="watch-ad-button"
          className="bg-primary text-black hover:bg-[#00C2D9] font-semibold shrink-0">
          <Gift className="h-4 w-4 mr-2" /> {done ? "Done" : "Watch & earn"}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) { clearInterval(timerRef.current); setOpen(false); } }}>
        <DialogContent className="bg-[#0F1015] border-white/10 max-w-lg" data-testid="rewarded-ad-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
              <Gift className="h-4 w-4 text-primary" /> Watch to earn +{status.reward}
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-[180px]">
            {AD_SLOT_REWARD ? <AdUnit slot={AD_SLOT_REWARD} label={false} /> : (
              <div className="h-44 rounded-xl bg-[#050508] flex items-center justify-center text-sm text-muted-foreground">Ad</div>
            )}
          </div>
          <Button onClick={claim} disabled={left > 0 || claiming} data-testid="claim-reward-button"
            className="w-full bg-primary text-black hover:bg-[#00C2D9] font-semibold h-11">
            {claiming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : left > 0 ? null : <Check className="h-4 w-4 mr-2" />}
            {left > 0 ? `Claim in ${left}s…` : `Claim +${status.reward} credit${status.reward === 1 ? "" : "s"}`}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
